import React, { useCallback, useMemo, useRef, useState } from "react";
import { Button as ShadcnButton } from "@/components/ui/button";
import { CopyIcon, type CopyIconHandle } from "@/components/ui/copy-icon";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Box, Text, VStack } from "@chakra-ui/react";
import { toast } from "sonner";
import { PineScriptCodeBlock } from "../Reports/PineScriptCodeBlock";
import { useReportStrategyPineRequirements } from "../../hooks/useReportStrategyPineRequirements";
import type { ReportStrategyResponse } from "../../types/reports";
import { toastError } from "../../utils/showApiErrorToast";
import {
  getInjectReadinessDisplay,
  getParseStatusDisplay,
  getPineScriptKindDisplay,
  isIndicatorPineScript,
  shouldShowInjectReadinessBadge,
} from "../../utils/pineIrUi";
import { extractPineScriptText } from "../../utils/reportStrategyPineUi";
import { mergeStrategyWithParseSummary } from "../../utils/strategyComposeUi";
import {
  buildStrategyPineEditorBadgeItems,
  PineBadgeList,
} from "../../views/Dashboard/Strategies/strategyTablePineBadges";
import { StrategyPineDetailDialog } from "./StrategyPineDetailDialog";
import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
  StrategyPineSectionCard,
} from "./StrategyPineSectionCard";

const PINE_SCRIPT_BLOCK_MAX_H = "min(60vh, 520px)";

function PineScriptCopyIconButton({
  disabled,
  ariaLabel,
  onClick,
}: {
  disabled: boolean;
  ariaLabel: string;
  onClick: () => void;
}): React.ReactElement {
  const copyIconRef = useRef<CopyIconHandle | null>(null);

  const startCopyIconAnimation = useCallback(() => {
    void copyIconRef.current?.startAnimation();
  }, []);

  const stopCopyIconAnimation = useCallback(() => {
    void copyIconRef.current?.stopAnimation();
  }, []);

  const copyIconHoverHandlers = useMemo(
    () => ({
      onPointerDownCapture: startCopyIconAnimation,
      onMouseEnter: startCopyIconAnimation,
      onMouseLeave: stopCopyIconAnimation,
    }),
    [startCopyIconAnimation, stopCopyIconAnimation]
  );

  return (
    <button
      type="button"
      data-pine-script-copy=""
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      {...copyIconHoverHandlers}
    >
      <CopyIcon ref={copyIconRef} size={16} className="shrink-0 text-current" aria-hidden />
    </button>
  );
}

export type StrategyMasterPineSummaryProps = {
  strategy: ReportStrategyResponse;
  strategyId: string;
  subtleText: string;
  textColor: string;
  borderColor: string;
  onEditMaster: () => void;
};

export function StrategyMasterPineSummary({
  strategy,
  strategyId,
  subtleText,
  textColor,
  onEditMaster,
}: StrategyMasterPineSummaryProps): React.ReactElement {
  const { data: requirements } = useReportStrategyPineRequirements();
  const merged = mergeStrategyWithParseSummary(strategy);
  const masterScript = extractPineScriptText(merged);
  const hasMasterScript = Boolean(masterScript.trim());
  const ps = strategy.pine_parse_summary;
  const isIndicator = isIndicatorPineScript(merged);
  const status = merged.pine_parse_status;
  const inject = merged.inject_readiness;
  const scriptKind = merged.pine_script_kind ?? (isIndicator ? "indicator" : "strategy");

  const [dialogOpen, setDialogOpen] = useState(false);

  const parseUx = getParseStatusDisplay(status, requirements?.parse_status_ux);
  const injectUx = getInjectReadinessDisplay(inject, requirements?.inject_readiness_ux);
  const kindUx = getPineScriptKindDisplay(scriptKind);

  const issueHint =
    ps?.parse_issues_top?.[0]?.title ??
    ps?.parse_issues_top?.[0]?.message ??
    (ps?.parse_issue_codes_top?.length ? ps.parse_issue_codes_top.slice(0, 2).join(", ") : null);

  const panelBadgeItems = useMemo(() => {
    const items = buildStrategyPineEditorBadgeItems(merged);
    if (ps?.pine_version) {
      items.push({
        id: "pine-version",
        variant: "pine-meta",
        label: ps.pine_version,
        title: `Pine version ${ps.pine_version}`,
        pineBadgeKind: "pine-version",
      });
    }
    return items;
  }, [merged, ps?.pine_version]);

  const headerParseBadgeItem = useMemo(
    () => panelBadgeItems.find((item) => item.id === "parse") ?? null,
    [panelBadgeItems]
  );

  const copyText = useCallback(async (text: string, label: string) => {
    if (!text.trim()) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${label}`);
    } catch {
      toastError("Copy failed");
    }
  }, []);

  const downloadText = useCallback((text: string, filename: string) => {
    if (!text.trim()) return;
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  return (
    <>
      <StrategyPineSectionCard
        data-strategy-pine-card="master"
        title="Master Pinescript"
        description="Your original TradingView script. Composed export is in Active TradingView Pinescript."
        headerBadge={headerParseBadgeItem}
        onViewDetails={() => setDialogOpen(true)}
      />

      <StrategyPineDetailDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        size="wide"
        title="Master Pinescript"
        description="Source Pinescript stored on this strategy (not the composed TradingView export)."
        showDismissButton={false}
        footer={
          <>
            <PineScriptCopyIconButton
              disabled={!hasMasterScript}
              ariaLabel="Copy master Pinescript"
              onClick={() => void copyText(masterScript, "master Pinescript")}
            />
            <ShadcnButton
              type="button"
              variant="outline-gray"
              size="sm"
              data-font="primary"
              data-pine-script-download=""
              className="shrink-0"
              disabled={!hasMasterScript}
              onClick={() => downloadText(masterScript, `${strategyId}-master.pine`)}
            >
              <span className="normal-case tracking-normal">Download</span>
            </ShadcnButton>
            <ShadcnButton
              type="button"
              variant="outline-gray"
              size="sm"
              data-font="primary"
              data-strategy-master-pine-edit=""
              className="shrink-0"
              onClick={onEditMaster}
            >
              <span className="normal-case tracking-normal">Edit master Pinescript</span>
            </ShadcnButton>
          </>
        }
      >
        <Box id="strategy-master-pine-panel" className="flex flex-col gap-3">
          {panelBadgeItems.length > 0 ? <PineBadgeList items={panelBadgeItems} /> : null}

          <VStack align="stretch" spacing={2} fontSize="sm" color={subtleText}>
            {kindUx ? <Text>{kindUx.description}</Text> : null}
            {parseUx && status !== "full" ? (
              <Text>
                <Text as="span" fontWeight="medium" color={textColor}>
                  {parseUx.title}:{" "}
                </Text>
                {parseUx.description}
              </Text>
            ) : null}
            {injectUx && shouldShowInjectReadinessBadge(inject) ? (
              <Text>
                <Text as="span" fontWeight="medium" color={textColor}>
                  {injectUx.title}:{" "}
                </Text>
                {injectUx.description}
              </Text>
            ) : null}
            {typeof ps?.entry_sites_count === "number" && !isIndicator ? (
              <Text>
                Entries detected: {ps.entry_sites_count}
                {typeof ps.injectable_entry_count === "number"
                  ? ` (${ps.injectable_entry_count} merge-ready)`
                  : ""}
              </Text>
            ) : null}
            {typeof ps?.exit_sites_count === "number" ? (
              <Text>Exit hooks: {ps.exit_sites_count} site(s)</Text>
            ) : null}
            {issueHint ? (
              <Text>
                Top issue:{" "}
                <Text as="span" color={textColor}>
                  {issueHint}
                </Text>
              </Text>
            ) : null}
            {!hasMasterScript && !status && !ps ? (
              <Text>
                No parse on file yet. Save Pinescript in Edit master or run Parse in the editor.
              </Text>
            ) : null}
          </VStack>

          <Field className="w-full" data-strategy-master-pine-script-field="">
            <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Master script</FieldLabel>
            <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
              Source Pinescript stored on this strategy (not the composed TradingView export).
            </FieldDescription>
            {hasMasterScript ? (
              <PineScriptCodeBlock code={masterScript} maxH={PINE_SCRIPT_BLOCK_MAX_H} brandChrome />
            ) : (
              <Text fontSize="sm" color={subtleText} fontFamily="var(--font-primary)">
                No master Pinescript saved yet.
              </Text>
            )}
          </Field>
        </Box>
      </StrategyPineDetailDialog>
    </>
  );
}
